import type { FC, JSX } from 'react'
import {
  ErrorBoundary,
  type ErrorBoundaryPropsWithComponent,
  type ErrorBoundaryPropsWithRender,
} from 'react-error-boundary'

import { ErrorFallback } from '../feedback/errorFallback/ErrorFallback'

/**
 * HOC for creating an error boundary around any component, thereby making the component easier to mount in a scenario
 * where it will auto mange its own error state.
 *
 * @example
 * const ErrorManagedComponent = withErrorBoundary(SomeComponent, {FallbackComponent: () => <Alert message="Something happened"/>})
 * return <ErrorManagedComponent {...someComponentProps} />
 */
export const withErrorBoundary = <T,>(
  WrappedComponent: FC<T>,
  boundaryProps:
    | ErrorBoundaryPropsWithComponent
    | ErrorBoundaryPropsWithRender = {
    FallbackComponent: ErrorFallback,
  },
): FC<T & JSX.IntrinsicAttributes> => {
  const ErrorManagedComponent = (props: T & JSX.IntrinsicAttributes) => (
    <ErrorBoundary {...boundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  )

  ErrorManagedComponent.displayName = `${
    WrappedComponent.displayName || WrappedComponent.name || 'Unknown'
  }ErrorManaged`

  return ErrorManagedComponent
}
